/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { IActionContext, IGridSettings, IImageSettings } from '@msdyn365-commerce/core';
import { Customer, TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    addThrottledEvent,
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    onTelemetryClick,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';

import { IMenuItemData, IHerbalContentNavigationMenuData, MenuSource, MenuTypeIndexId } from './herbal-content-navigation-menu.data';
import { IHerbalContentNavigationMenuConfig } from './herbal-content-navigation-menu.props.autogenerated';

import {
    ICmsLeftNavItemsData,
    IHerbalContentNavigationMenuProps,
    ISubMenusData,
    navigationMenuSource
} from './herbal-content-navigation-menu.props.autogenerated';

export interface INavigationMenuViewProps extends IHerbalContentNavigationMenuProps<IHerbalContentNavigationMenuData> {
    className: string;
    logoImage?: React.ReactNode;
    logoImageContainer: INodeProps;
    leftMenuItemData: IMenuItemData[];
    rightMenuItemData: IMenuItemData[];
    Navigation: IModuleProps;
    MenuList: INodeProps;
    ListItem: INodeProps;
    Link: INodeProps;
    Button: INodeProps;
    Span: INodeProps;
    ImageContainer: INodeProps;
    MobileDescriptionContainer: INodeProps;
    MobileBackButton: INodeProps;
    MobileDescriptionLabel: INodeProps;
    imageSettings: IImageSettings;
    gridSettings: IGridSettings;
    isMobileView: boolean;
    DivContainer?: INodeProps;
    ImageDivContainer?: INodeProps;
    showCategoryImage?: boolean;
    showPromotionalContent?: boolean;
    signInLink?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
    isMobile: boolean;
}

/**
 * Site-builder configuration for the module.
 */
export interface IHerbalContentNavigationMenuFullConfig extends IHerbalContentNavigationMenuConfig {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- This field comes from SDK.
    msdyn365__moduleLayout?: string;
}

/**
 *
 * NavigationMenu component.
 * @extends {React.PureComponent<IHerbalContentNavigationMenuProps<IHerbalContentNavigationMenuData>>}
 */
class NavigationMenu extends React.PureComponent<
    IHerbalContentNavigationMenuProps<IHerbalContentNavigationMenuData>,
    INavigationMenuState
> {
    private static CMSItemIdInit: number = MenuTypeIndexId.Cms;

    private static RSItemIdInit: number = MenuTypeIndexId.Retail;

    private resizeThrottledEventHandler?: (event: Event) => void;

    private leftMenuItemList: IMenuItemData[];
    private rightMenuItemList: IMenuItemData[];

    // @ts-ignore
    private readonly telemetryContent: ITelemetryContent;

    private isCMS: boolean = true;
    private originURL: string = '';

    @observable
    private viewport: GridSize;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: IHerbalContentNavigationMenuProps<IHerbalContentNavigationMenuData>) {
        super(props);
        this._updateViewport = this._updateViewport.bind(this);
        this.viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._setNavigationSource(this.props.config && this.props.config.navigationMenuSource);
        this.state = { isMobile: this.isMobile };
        this.leftMenuItemList = [];
        this.rightMenuItemList = [];
    }

    @computed get isMobile(): boolean {
        return this.viewport === 'xs' || this.viewport === 'sm';
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._updateViewport as EventListener);
        }
        this._updateViewport();
        this.originURL =
            this.props.context.actionContext.requestContext.app.config.ecomBaseUrl &&
            this.props.context.actionContext.requestContext.app.config.ecomBaseUrl;
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
        }
    }

    public render(): JSX.Element | null {
        const {
            config,
            context: {
                request: { user }
            }
        } = this.props;
        const { logoImage, enableMenuFollowUser } = this.props.config;

        if (!(this.isCMS && config.cmsLeftNavItems && config.cmsLeftNavItems.length > 0)) {
            return null;
        }
        if (!(this.isCMS && config.cmsRightNavItems && config.cmsRightNavItems.length > 0)) {
            return null;
        }

        const customer = this.props.data.accountInformation?.result;
        if (enableMenuFollowUser !== 'all') {
            if (enableMenuFollowUser === 'isNotLogin' && customer) {
                return null;
            } else if (enableMenuFollowUser === 'isLogin' && !customer) {
                return null;
            }
        }

        this.leftMenuItemList = this._mapData(config.cmsLeftNavItems);
        this.rightMenuItemList = this._mapData(config.cmsRightNavItems);

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=224&h=300&m=6', w: 0, h: 0 },
                xl: { q: 'w=224&h=300&m=6', w: 0, h: 0 }
            },
            lazyload: true
        };

        const logoImageProps = {
            gridSettings: this.props.context.request.gridSettings ?? {},
            imageSettings: logoImage?.imageSettings
        };

        const contentBlockImage = (
            <a href={this.props.config.logoLinkText} className={'ms-link-logo'}>
                {/*
                // @ts-ignore */}
                <Msdyn365.Image
                    {...logoImage}
                    {...logoImageProps}
                    requestContext={this.props.context.request}
                    editProps={{
                        key: this.props.config.logoImage ?? {},
                        requestContext: this.props.context.request,
                        moduleType: this.props.typeName,
                        imagePropertyName: 'image',
                        moduleId: this.props.id,
                        layout: (this.props.config as IHerbalContentNavigationMenuFullConfig).msdyn365__moduleLayout
                    }}
                    shouldSkipToMainImage
                />
            </a>
        );

        const viewProps = {
            ...this.props,
            ...this.state,
            imageSettings: defaultImageSettings,
            gridSettings: this.props.context.request.gridSettings,
            Navigation: {
                moduleProps: this.props,
                className: classnames('ms-nav', this.props.config.className, this.state.isMobile ? 'mobile-vp' : 'desktop-vp'),
                tag: 'nav',
                role: this.isMobile ? 'listbox' : 'navigation',
                'aria-label': this.props.resources.menuAriaLabel
            },
            MenuList: {
                className: 'ms-nav__list',
                tag: 'ul',
                role: 'menu'
            },
            ListItem: {
                className: 'ms-nav__list__item',
                role: 'presentation',
                tag: 'li'
            },
            showCategoryImage: this.props.config.displayCategoryImage || false,
            showPromotionalContent: this.props.config.displayPromotionalImage || false,
            Link: {
                tag: 'a',
                className: 'ms-nav__list__item__link',
                role: this.isMobile ? 'option' : 'menuitem'
            },
            DivContainer: {
                tag: 'div',
                className: 'ms-nav__feature'
            },
            ImageDivContainer: {
                tag: 'div',
                className: 'category-image',
                role: 'tabpanel'
            },
            Button: {
                className: 'ms-nav__list__item__button',
                tag: 'button',
                role: this.isMobile ? 'option' : 'menuitem',
                onClick: this.props
            },
            ImageContainer: {
                className: 'ms-nav__list__item__image'
            },
            Span: {
                className: 'ms-nav__list__item__span',
                tag: 'span'
            },
            MobileDescriptionContainer: {
                className: 'ms-nav__list__mobile__container'
            },
            MobileBackButton: {
                className: 'ms-nav__list__mobile__container__button',
                tag: 'button',
                'aria-label': this.props.resources.backButtonAriaLabel,
                onClick: this.props
            },
            MobileDescriptionLabel: {
                className: 'ms-nav__list__mobile__container__span',
                tag: 'span'
            },
            isMobileView: this.state.isMobile,
            leftMenuItemData: this.leftMenuItemList,
            rightMenuItemData: this.rightMenuItemList,
            logoImage: contentBlockImage,
            signInLink: this._getSigninButton(customer, user),
            logoImageContainer: { className: 'ms-site-logo' },
            className: classnames('ms-nav', this.props.config.className)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _mapData(navItems: IMenuItemData[]): IMenuItemData[] {
        const mergedData: IMenuItemData[] = [];
        if (this.isCMS && navItems && navItems.length > 0) {
            navItems.map((item: ICmsLeftNavItemsData, idx: number) =>
                mergedData.push(this._constructMenuItemData(undefined, item, false, idx + 1))
            );
        }
        return this._constructTopMenuItemData(mergedData);
    }

    private _constructTopMenuItemData(mergedData: IMenuItemData[]): IMenuItemData[] {
        const enableTopLevelMenu = this.props.config.enabletopMenu || false;
        if (!enableTopLevelMenu) {
            return mergedData;
        }
        const mergedRootMenuData: IMenuItemData[] = [];
        const topMenu = {
            id: 1,
            linkText: this.props.config.rootMenuNavigation || '...',
            linkURL: '#',
            imageSource: undefined,
            subMenu: mergedData
        } as IMenuItemData;
        mergedRootMenuData.push(topMenu);

        return mergedRootMenuData;
    }

    private _constructMenuItemData(
        retailItem?: CategoryHierarchy,
        cmsItem?: ICmsLeftNavItemsData,
        isRetail?: boolean,
        idx?: number,
        addedAllMenu?: boolean
    ): IMenuItemData {
        const locale =
            this.props.context && this.props.context.request && this.props.context.request.locale
                ? this.props.context.request.locale
                : 'en-us';
        const { categoryPromotionalContent } = this.props.config;

        // Filter category promotional data based on category name
        const promotionalData =
            retailItem &&
            categoryPromotionalContent?.filter(promoItem => promoItem.categoryName?.toLowerCase() === retailItem?.Name?.toLowerCase());

        // First check if image is available from promotional data else read from retail server
        let imageSource: string = 'empty';
        if (ArrayExtensions.hasElements(retailItem?.Images) && retailItem!.Images[0].Uri) {
            imageSource = retailItem!.Images[0].Uri;
        }
        if (isRetail) {
            return {
                id: NavigationMenu.RSItemIdInit++,
                linkText: NavigationMenu.getFriendlyName(locale, retailItem?.NameTranslations) || '',
                linkURL: retailItem?.Url,
                imageSource,
                subMenu: this._getNestedChildren(retailItem, imageSource, addedAllMenu),
                promotionalContent: this.props.config.displayPromotionalImage && promotionalData,
                menuSource: MenuSource.Retail
            } as IMenuItemData;
        }
        return {
            id: NavigationMenu.CMSItemIdInit++,
            linkText: (cmsItem && cmsItem.linkText) || '',
            linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
            imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
            imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
            subMenu: this._getCMSNestedChildren(cmsItem),
            menuSource: MenuSource.Cms,
            shouldOpenNewTab: cmsItem?.openInNewTab
        } as IMenuItemData;
    }

    private _getNestedChildren(category?: CategoryHierarchy, imageSourceUrl?: string, addedAllMenu: boolean = false): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];
        if (!category || !category.Children || category.Children.length === 0) {
            return [];
        }
        if (!this.props.config.enabletopMenu && addedAllMenu) {
            nestedChildren.push({
                linkText: this.props.resources.allCategoryMenuText,
                imageSource: imageSourceUrl,
                linkURL: getCategoryUrl(category, (this.props.context as unknown) as IActionContext, {})
            });
        }
        category.Children.map((item: CategoryHierarchy, index) => {
            nestedChildren.push(this._constructMenuItemData(item, undefined, true, index, false));
        });
        return nestedChildren;
    }

    private _getCMSNestedChildren(category?: ICmsLeftNavItemsData): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];

        if (!category || !category.subMenus || category.subMenus.length === 0) {
            return [];
        }

        category.subMenus.map((item: ISubMenusData, idx: number) => {
            nestedChildren.push(this._constructMenuItemData(undefined, item, false, idx));
        });
        return nestedChildren;
    }

    private readonly _setNavigationSource = (sourceType?: navigationMenuSource): void => {
        switch (sourceType) {
            case navigationMenuSource.retailServerOnly:
                this.isCMS = false;
                break;
            case navigationMenuSource.cmsOnly:
                this.isCMS = true;
                break;
            case navigationMenuSource.all:
                this.isCMS = true;
                break;
            default:
        }
    };

    private _updateViewport(): void {
        this.viewport = this._getViewport();
        this.setState({ isMobile: this.isMobile });
    }

    private _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth && window.screen) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                const width = Math.min(window.screen.width, window.innerWidth);
                if (gridSettings.xs && width <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && width <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && width <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && width <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    }

    // @ts-ignore
    private _getSigninButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        const { labelLoginButton } = this.props.config;
        const loginCallback = Msdyn365.getUrlSync('logincallback', this.props.context.actionContext);
        const signInUrl =
            user &&
            (loginCallback && loginCallback !== '/logincallback'
                ? `${user.signInUrl}?ru=${this.originURL}${loginCallback}`
                : user.signInUrl);
        return (
            !customer && (
                <Button
                    className='ms-header__signin-button'
                    title={labelLoginButton}
                    href={signInUrl}
                    aria-label={labelLoginButton}
                    onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Practitioner Login')}
                    {...attributes}
                >
                    <span className='ms-header__signin-button-text' aria-hidden>
                        {labelLoginButton}
                    </span>
                </Button>
            )
        );
    }
}

export default NavigationMenu;
